<script lang="ts">
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import Simple from './partials/Simple.svelte';
  import DetailAlert from './partials/DetailAlert.svelte';
  import { AlertType } from './AlertProps';
  import type { AlertDetailedInfo } from '$shared/models/AlertDetailedInfo';
  import type { AlertOption } from '$provider/models/AlertOption';
  import { onMount } from 'svelte';

  const CloseIconColorMap: ObjectType<string> = {
    [AlertType.Error]: 'red',
    [AlertType.Info]: 'white',
    [AlertType.Warn]: 'grey'
  };

  interface Props {
    data: string | AlertDetailedInfo | undefined | null;
    maxWidth?: boolean;
    closable?: boolean;
    options?: AlertOption;
    autoScroll?: boolean;
    children?: import('svelte').Snippet;
    onclose?: () => void;
    onclick?: (e: MouseEvent) => void;
  }

  let {
    data,
    maxWidth = false,
    closable = false,
    options = {} as AlertOption,
    autoScroll = false,
    children,
    onclose,
    onclick
  }: Props = $props();

  let divElement: HTMLDivElement = $state();

  let component = $derived(typeof data === 'object' ? DetailAlert : Simple);
  let type = $derived(options?.type || AlertType.Info);
  let iconColor = $derived(CloseIconColorMap[type] || '');

  onMount(() => {
    if (options?.closeIn) {
      const timer = setTimeout(() => {
        onClickClose();
      }, options?.closeIn);
      return () => clearTimeout(timer);
    }
    autoScroll && divElement.scrollIntoView({ behavior: 'smooth' });
  });

  const onClickClose = () => {
    onclose?.();
  };

  const SvelteComponent = $derived(component);
</script>

<div bind:this={divElement} class="comp-alert {type}" class:maxWidth data-cy="alert">
  <SvelteComponent {data} {options} {onclick}>
    {@render children?.()}
    {#if closable}
      <button data-cy="close-alert" type="button" class="close-button" onclick={onClickClose}>
        <Icon value="icon-x {iconColor}" />
      </button>
    {/if}
  </SvelteComponent>
</div>

<style>
  .comp-alert {
    position: relative;
    width: auto;
    min-width: 200px;
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    padding: 4px 40px 4px 24px;
    box-shadow: 1px 10px 27px -10px rgba(0, 0, 0, 0.25);
    border-radius: 5px;
    background-color: var(--grey-dark);
    color: #fff;
  }
  .comp-alert.warn {
    background-color: var(--yellow-light);
    color: var(--yellow-dark);
  }
  .comp-alert.primary {
    background-color: var(--light-blue);
    color: var(--blue);
  }
  .comp-alert.error {
    background-color: var(--red-light);
    color: var(--red);
  }
  .comp-alert.maxWidth {
    width: 100%;
  }
  .close-button {
    width: 32px;
    height: 32px;
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
  }
</style>
